import { Observable } from 'rxjs';

/**
 * Every kind of row the hierarchical table can render. Only `record` rows carry a record; the rest are
 * structural rows the tree introduces (sub-headers, submap group headers, skeletons while children load,
 * "Toon meer", the weesdocumenten placeholder and inline empty/error messages).
 */
export type RowKind = 'record' | 'placeholder' | 'subheader' | 'submap' | 'skeleton' | 'show-more' | 'message';

/**
 * One visual row. `key` is what the template tracks by: a record id for record rows and a synthetic id
 * (`'orphans'`, `'subheader:doss-12'`) for the rest — never a position, because child rows interleave
 * with their parents and a positional key would fail silently.
 */
export interface HierarchyRow<T> {
    readonly kind: RowKind;
    readonly key: string;
    readonly level: number;
    readonly parentKey: string | null;
    /** Absent on every non-record kind. Stays an observable: it is what re-renders a row after enrichment. */
    readonly record$?: Observable<T>;
    readonly selectable: boolean;
    /** Record rows: whether the row can be opened at all, and whether it currently is. */
    readonly expandable?: boolean;
    readonly expanded?: boolean;
    /**
     * The folder inside its uploaded ZIP a record came from. Absent where there is no path to show, which is
     * every dossier row.
     *
     * It is carried by two kinds, because the same value is presented two ways: a `record` row captions it
     * above its own content, and a `submap` row *is* it — one header standing above the group of stukken that
     * share it. Which of the two a stuk gets is {@link grouped}'s business, not this field's; a grouped stuk
     * keeps its own value regardless, so its visually-hidden label still announces where it came from.
     */
    readonly pathCaption?: string | null;
    /**
     * Record rows: whether the row sits in an expansion the submap headers have taken over. Then the header
     * above carries the path visibly and this row does not caption it again — the one repetition HFBD-74 is
     * about. A stuk at the dossier root is `grouped` too, and gets no header: it has no folder to name, and
     * captioning its path would print the filename the header strips.
     */
    readonly grouped?: boolean;
    readonly parentCaption?: string | null;
    /**
     * Record rows: the folder alone — {@link pathCaption} without the filename it ends in. It is both what the
     * grouping keys a stuk by and what tells a grouped row whether a header stands above it: `''` for a stuk at
     * the dossier root, which is grouped but headed by nothing, and so carries no connector either.
     */
    readonly submap?: string;
    /** `message` rows: the translation key to render, and whether the row offers the fetch again. */
    readonly messageKey?: string;
    readonly messageParams?: Record<string, string | number>;
    readonly canRetry?: boolean;
    /** `show-more` rows: how many children exist beyond the ones already loaded. */
    readonly remaining?: number;
}

/**
 * How far one dossier's expansion has got. Owned by the container; the table only renders its rows.
 *
 * ⚠ `childRows` holds **record rows only**. `loadedCount` is its length and doubles as the start index of the
 * next batch, so a display row inserted here — a submap header, say — would make "Toon meer" fetch from the
 * wrong offset and silently skip records. Display rows are added when the expansion is flattened for
 * rendering, never written back.
 *
 * `totalCount` is `null` while the total is unknown, which is not the same as knowing there are none: an
 * expansion whose total never arrived is treated as still loading rather than as complete.
 */
export interface ExpansionState<T> {
    readonly status: 'loading' | 'ready' | 'empty' | 'error';
    readonly childRows: HierarchyRow<T>[];
    readonly loadedCount: number;
    readonly totalCount: number | null;
}

/** How many skeleton rows stand in for an expansion that is still loading its first batch. */
export const SKELETON_ROW_COUNT: number = 3;

/**
 * Builds the top-level row for a record that is already on the page. `pathCaption` belongs here rather than on
 * the record stream because the caption is a property of the *row*: the table stays generic over its record
 * type, and reading a path out of one would give it knowledge of the record shape it deliberately has none of.
 */
export function toRecordRow<T>(
    key: string,
    record$: Observable<T>,
    selectable: boolean = true,
    options: { expandable?: boolean; expanded?: boolean; pathCaption?: string | null; submap?: string } = {}
): HierarchyRow<T> {
    return {
        kind: 'record',
        key,
        level: 0,
        parentKey: null,
        record$,
        selectable,
        expandable: options.expandable ?? false,
        expanded: options.expanded ?? false,
        pathCaption: options.pathCaption ?? null,
        // HFBD-85: `''` is "no folder", which is what a dossier row and a stuk at the dossier root both are.
        submap: options.submap ?? ''
    };
}

/**
 * A stuk revealed by expanding the row above it — a dossier, or a stuk holding stukken of its own. `expanded`
 * is applied on the way to the screen rather than stored here.
 *
 * `selectable` is off unless the overview opts in: a checkbox here selects the stuk *itself*, claiming nothing
 * about the dossier above it, and only the pages whose bulk actions can act on a stuk should offer one.
 */
export function toChildRow<T>(
    key: string,
    parentKey: string,
    record$: Observable<T>,
    pathCaption: string | null = null,
    submap: string = '',
    selectable: boolean = false,
    options: { level?: number; expandable?: boolean; parentCaption?: string | null } = {}
): HierarchyRow<T> {
    return {
        kind: 'record',
        key,
        level: options.level ?? 1,
        parentKey,
        record$,
        selectable,
        pathCaption,
        submap,
        parentCaption: options.parentCaption ?? null,
        expandable: options.expandable ?? false,
        expanded: false
    };
}

/** Introduces an expansion: a tinted band carrying the stuk level's column labels, and no controls. */
export function toSubheaderRow<T>(parentKey: string, level: number = 1): HierarchyRow<T> {
    return { kind: 'subheader', key: `subheader:${parentKey}`, level, parentKey, selectable: false };
}

/**
 * Names the folder the stukken below it share, once instead of on every one of them.
 *
 * The key carries both the dossier and the whole path: `parentKey` keeps two dossiers holding a same-named
 * folder apart, and the full path keeps two same-named folders under different parents apart within one
 * dossier — so it stays unique and non-positional, as the row model requires.
 *
 * HFBD-85: `level` is the group's own, because a detail page's stukken are top-level rows. The table picks a
 * column set per row by level, so a level-1 header over a level-0 group would resolve the *child* set — which
 * on a flat page is null and works only through the table's `childColumns ?? columns` fallback.
 */
export function toSubmapRow<T>(parentKey: string, path: string, level: number = 1): HierarchyRow<T> {
    return {
        kind: 'submap',
        key: `submap:${parentKey}:${path}`,
        level,
        parentKey,
        selectable: false,
        pathCaption: path
    };
}

/** Greyed stand-ins shaped like stuk rows, so nothing jumps when the real children land. */
export function toSkeletonRows<T>(
    parentKey: string,
    count: number = SKELETON_ROW_COUNT,
    level: number = 1
): HierarchyRow<T>[] {
    return Array.from({ length: count }, (_, index) => ({
        kind: 'skeleton' as const,
        key: `skeleton:${parentKey}:${index}`,
        level,
        parentKey,
        selectable: false
    }));
}

export function toMessageRow<T>(
    parentKey: string,
    messageKey: string,
    canRetry: boolean = false,
    options: { keySuffix?: string; messageParams?: Record<string, string | number>; level?: number } = {}
): HierarchyRow<T> {
    return {
        kind: 'message',
        key: `message:${parentKey}${options.keySuffix ? `:${options.keySuffix}` : ''}`,
        level: options.level ?? 1,
        parentKey,
        selectable: false,
        messageKey,
        messageParams: options.messageParams,
        canRetry
    };
}

/** Closes a capped batch: fetches the next 25 children of `parentKey`. */
export function toShowMoreRow<T>(parentKey: string, remaining: number, level: number = 1): HierarchyRow<T> {
    return { kind: 'show-more', key: `show-more:${parentKey}`, level, parentKey, selectable: false, remaining };
}
